import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import ViewSelector from "../components/ViewSelector";
import {
  Users,
  Loader2,
  Mail,
  Phone,
  Briefcase,
  Building,
  Search,
  Filter,
  UserCheck,
  UserX,
  Edit2,
  Shield,
  LayoutGrid,
  List,
  Table,
  Eye,
  Package,
  Ticket,
  Palmtree,
  Clock,
  GraduationCap,
  FileCheck,
  Receipt,
  ClipboardList,
  AlertTriangle
} from "lucide-react";

// Un empleado "sin departamento" no tiene nombre de departamento resuelto
const sinDepartamento = (emp) => !(emp?.departamento_nombre || emp?.department);

export default function EmpleadosPage() {
  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, inactive
  const [soloSinDepto, setSoloSinDepto] = useState(false); // filtra empleados sin departamento
  const [currentView, setCurrentView] = useState("cards"); // cards, table, list
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingEmpleado, setEditingEmpleado] = useState(null);
  const [saving, setSaving] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  const [stats, setStats] = useState({ total: 0, activos: 0, inactivos: 0, sinDepartamento: 0 });
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      // Obtener todos los usuarios para estadísticas
      const allRes = await axios.get(`${API}/users`, getAuthHeaders());
      const allUsers = allRes.data;
      
      // Calcular estadísticas
      const activos = allUsers.filter(u => u.is_active !== false).length;
      const inactivos = allUsers.filter(u => u.is_active === false).length;
      // Empleados activos sin departamento asignado (para que GH complete el perfil)
      const sinDepto = allUsers.filter(u => u.is_active !== false && sinDepartamento(u)).length;
      setStats({ total: allUsers.length, activos, inactivos, sinDepartamento: sinDepto });
      
      // Filtrar según el estado seleccionado
      let filtered = allUsers;
      if (statusFilter === "active") {
        filtered = allUsers.filter(u => u.is_active !== false);
      } else if (statusFilter === "inactive") {
        filtered = allUsers.filter(u => u.is_active === false);
      }
      
      setEmpleados(filtered);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Error al cargar empleados");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredEmpleados = empleados.filter(emp => {
    const matchesSearch = 
      emp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.position?.toLowerCase().includes(searchQuery.toLowerCase());
    if (soloSinDepto && !sinDepartamento(emp)) return false;
    return matchesSearch;
  });

  const openEditDialog = (empleado) => {
    setEditingEmpleado({
      ...empleado,
      is_active: empleado.is_active !== false
    });
    setShowEditDialog(true);
  };

  const openProfile = async (empleado) => {
    setProfileData(null);
    setShowProfileDialog(true);
    setProfileLoading(true);
    try {
      const res = await axios.get(`${API}/empleados/${empleado.id}/resumen`, getAuthHeaders());
      setProfileData(res.data);
    } catch (err) {
      console.error("Error loading profile:", err);
      toast.error("Error al cargar resumen del empleado");
      setShowProfileDialog(false);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleToggleActive = async () => {
    if (!editingEmpleado) return;
    
    setSaving(true);
    try {
      const newStatus = !editingEmpleado.is_active;
      await axios.put(`${API}/users/${editingEmpleado.id}`, {
        ...editingEmpleado,
        is_active: newStatus
      }, getAuthHeaders());
      
      toast.success(newStatus ? "Empleado activado" : "Empleado desactivado");
      setShowEditDialog(false);
      fetchData();
    } catch (err) {
      toast.error("Error al actualizar estado");
    } finally {
      setSaving(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      administrador: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
      admin: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
      analyst: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      analista: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
      viewer: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
      visualizador: "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300",
      usuario: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
    };
    const labels = {
      administrador: "Administrador",
      admin: "Administrador",
      analyst: "Analista",
      analista: "Analista",
      viewer: "Visualizador",
      visualizador: "Visualizador",
      usuario: "Usuario"
    };
    return (
      <Badge className={`${styles[role?.toLowerCase()] || styles.usuario} border-0 text-xs`}>
        {labels[role?.toLowerCase()] || role || "Usuario"}
      </Badge>
    );
  };

  const isAdmin = user?.role?.toLowerCase() === 'administrador' || user?.role?.toLowerCase() === 'admin';

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="empleados-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Directorio de Empleados
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">
              Usuarios activos del sistema
            </p>
          </div>
        </div>

        {/* Stats Cards - Clickable filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "all" && !searchQuery && !soloSinDepto ? "ring-2 ring-slate-500" : ""}`}
            onClick={() => { setStatusFilter("all"); setSearchQuery(""); setSoloSinDepto(false); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Total Empleados</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-slate-400" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "active" && !soloSinDepto ? "ring-2 ring-green-500" : ""}`}
            onClick={() => { setStatusFilter("active"); setSearchQuery(""); setSoloSinDepto(false); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Activos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.activos}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-400" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${statusFilter === "inactive" && !soloSinDepto ? "ring-2 ring-red-500" : ""}`}
            onClick={() => { setStatusFilter("inactive"); setSearchQuery(""); setSoloSinDepto(false); }}
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Inactivos</p>
                  <p className="text-2xl font-bold text-red-600">{stats.inactivos}</p>
                </div>
                <UserX className="w-8 h-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card 
            className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 cursor-pointer transition-all hover:shadow-md hover:scale-[1.02] ${soloSinDepto ? "ring-2 ring-amber-500" : ""}`}
            onClick={() => { setSoloSinDepto(v => !v); setStatusFilter("all"); setSearchQuery(""); }}
            data-testid="filter-sin-departamento"
          >
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Sin Departamento</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.sinDepartamento}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6 items-center justify-between">
          <div className="flex gap-4 items-center flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, email, departamento..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-empleados"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="filter-status">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="active">Solo Activos</SelectItem>
                <SelectItem value="inactive">Solo Inactivos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <ViewSelector currentView={currentView} onViewChange={setCurrentView} />
        </div>

        {/* Employees View */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : filteredEmpleados.length === 0 ? (
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {searchQuery ? "No se encontraron empleados con ese criterio" : "No hay empleados registrados"}
              </p>
            </CardContent>
          </Card>
        ) : currentView === "table" ? (
          /* Table View */
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-slate-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Empleado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Departamento</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Rol</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Estado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                    {filteredEmpleados.map((emp) => (
                      <tr key={emp.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${emp.is_active === false ? 'opacity-60' : ''} ${sinDepartamento(emp) && emp.is_active !== false ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`} data-testid={`emp-row-${emp.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${emp.is_active === false ? 'bg-slate-400' : 'bg-[#0C314C]'}`}>
                              <span className="text-sm font-semibold text-white">{emp.name?.charAt(0).toUpperCase() || '?'}</span>
                            </div>
                            <span className="font-medium text-slate-900 dark:text-white">{emp.name || emp.email?.split('@')[0]}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{emp.email}</td>
                        <td className="px-4 py-3 text-sm">
                          {sinDepartamento(emp) ? (
                            <Badge className="border-0 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" data-testid={`emp-sin-depto-${emp.id}`}>
                              <AlertTriangle className="w-3 h-3 mr-1" /> Sin departamento
                            </Badge>
                          ) : (
                            <span className="text-slate-600 dark:text-slate-400">{emp.departamento_nombre || emp.department}</span>
                          )}
                        </td>
                        <td className="px-4 py-3">{getRoleBadge(emp.role)}</td>
                        <td className="px-4 py-3">
                          <Badge className={`border-0 text-xs ${emp.is_active !== false ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                            {emp.is_active !== false ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </td>
                        {isAdmin && (
                          <td className="px-4 py-3 text-center">
                            <div className="flex justify-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openProfile(emp)}>
                                <Eye className="w-4 h-4 text-slate-400" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)}>
                                <Edit2 className="w-4 h-4 text-slate-400" />
                              </Button>
                            </div>
                          </td>
                        )}
                        {!isAdmin && (
                          <td className="px-4 py-3 text-center">
                            <Button variant="ghost" size="sm" onClick={() => openProfile(emp)}>
                              <Eye className="w-4 h-4 text-slate-400" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        ) : currentView === "list" ? (
          /* List View */
          <Card className="dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-2">
              <div className="divide-y divide-slate-200 dark:divide-slate-700">
                {filteredEmpleados.map((emp) => (
                  <div key={emp.id} className={`flex items-center justify-between py-3 px-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded ${emp.is_active === false ? 'opacity-60' : ''} ${sinDepartamento(emp) && emp.is_active !== false ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`} data-testid={`emp-list-${emp.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${emp.is_active === false ? 'bg-slate-400' : 'bg-[#0C314C]'}`}>
                        <span className="text-sm font-semibold text-white">{emp.name?.charAt(0).toUpperCase() || '?'}</span>
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{emp.name || emp.email?.split('@')[0]}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{emp.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {sinDepartamento(emp) && (
                        <Badge className="border-0 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                          <AlertTriangle className="w-3 h-3 mr-1" /> Sin departamento
                        </Badge>
                      )}
                      {getRoleBadge(emp.role)}
                      <Badge className={`border-0 text-xs ${emp.is_active !== false ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`}>
                        {emp.is_active !== false ? 'Activo' : 'Inactivo'}
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => openProfile(emp)}>
                        <Eye className="w-4 h-4 text-slate-400" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)}>
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          /* Cards View (default) */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredEmpleados.map((emp) => (
              <Card 
                key={emp.id} 
                className={`dark:bg-slate-800 dark:border-slate-700 hover:shadow-lg transition-shadow ${
                  emp.is_active === false ? 'opacity-60' : ''
                } ${sinDepartamento(emp) && emp.is_active !== false ? 'ring-2 ring-amber-400 dark:ring-amber-500/60' : ''}`}
                data-testid={`emp-card-${emp.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                        emp.is_active === false ? 'bg-slate-400' : 'bg-[#0C314C]'
                      }`}>
                        <span className="text-xl font-semibold text-white">
                          {emp.name?.charAt(0).toUpperCase() || emp.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <CardTitle className="text-lg dark:text-white">{emp.name || emp.email?.split('@')[0]}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          {getRoleBadge(emp.role)}
                          <Badge 
                            className={`border-0 text-xs ${
                              emp.is_active !== false 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' 
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                            }`}
                          >
                            {emp.is_active !== false ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openProfile(emp)} data-testid={`btn-view-${emp.id}`}>
                        <Eye className="w-4 h-4 text-slate-400" />
                      </Button>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" onClick={() => openEditDialog(emp)}>
                          <Edit2 className="w-4 h-4 text-slate-400" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                      <Mail className="w-4 h-4" />
                      {emp.email}
                    </div>
                    {emp.phone && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Phone className="w-4 h-4" />
                        {emp.phone}
                      </div>
                    )}
                    {emp.position && (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Briefcase className="w-4 h-4" />
                        {emp.position}
                      </div>
                    )}
                    {(emp.departamento_nombre || emp.department) ? (
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <Building className="w-4 h-4" />
                        {emp.departamento_nombre || emp.department}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium" data-testid={`emp-card-sin-depto-${emp.id}`}>
                        <AlertTriangle className="w-4 h-4" />
                        Sin departamento asignado
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0C314C]" />
                Estado del Empleado
              </DialogTitle>
            </DialogHeader>
            
            {editingEmpleado && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    editingEmpleado.is_active ? 'bg-[#0C314C]' : 'bg-slate-400'
                  }`}>
                    <span className="text-lg font-semibold text-white">
                      {editingEmpleado.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{editingEmpleado.name}</p>
                    <p className="text-sm text-slate-500">{editingEmpleado.email}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg dark:border-slate-700">
                  <div>
                    <Label className="text-base">Estado Activo</Label>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {editingEmpleado.is_active 
                        ? "El empleado tiene acceso al sistema" 
                        : "El empleado NO puede acceder al sistema"}
                    </p>
                  </div>
                  <Switch
                    checked={editingEmpleado.is_active}
                    onCheckedChange={(checked) => setEditingEmpleado({...editingEmpleado, is_active: checked})}
                  />
                </div>

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-700 dark:text-amber-300">
                    <strong>Nota:</strong> Desactivar un empleado impedirá que pueda iniciar sesión en el sistema.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleToggleActive}
                disabled={saving}
                className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Guardar Cambios
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Profile Summary Dialog */}
        <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0C314C]" />
                Resumen del Empleado
              </DialogTitle>
            </DialogHeader>

            {profileLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#0C314C]" />
              </div>
            ) : profileData ? (
              <div className="space-y-4" data-testid="employee-summary">
                {/* Employee Header */}
                <div className="flex items-center gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${profileData.empleado.is_active ? 'bg-[#0C314C]' : 'bg-slate-400'}`}>
                    <span className="text-xl font-semibold text-white">
                      {profileData.empleado.name?.charAt(0).toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white">{profileData.empleado.name}</h3>
                    <p className="text-sm text-slate-500">{profileData.empleado.email}</p>
                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                      {profileData.empleado.position && <span>{profileData.empleado.position}</span>}
                      {profileData.empleado.position && profileData.empleado.department && <span>·</span>}
                      {profileData.empleado.department && <span>{profileData.empleado.department}</span>}
                    </div>
                  </div>
                  <Badge className={`border-0 ${profileData.empleado.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {profileData.empleado.is_active ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>

                {/* Mensajería */}
                <SummarySection icon={<Package className="w-4 h-4" />} title="Mensajería" color="blue">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatBox label="Creadas" value={profileData.mensajeria.creadas} />
                    <StatBox label="Pendientes" value={profileData.mensajeria.pendientes} color="yellow" />
                    <StatBox label="En Proceso" value={profileData.mensajeria.en_proceso} color="blue" />
                    <StatBox label="Completadas" value={profileData.mensajeria.completadas} color="green" />
                    <StatBox label="Devueltas" value={profileData.mensajeria.devueltas} color="orange" />
                    <StatBox label="Como Mensajero" value={profileData.mensajeria.como_mensajero} />
                    <StatBox label="Entregadas (Mensajero)" value={profileData.mensajeria.entregadas_como_mensajero} color="green" />
                  </div>
                </SummarySection>

                {/* Tickets */}
                <SummarySection icon={<Ticket className="w-4 h-4" />} title="Mesa de Ayuda" color="purple">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatBox label="Creados" value={profileData.tickets.creados} />
                    <StatBox label="Abiertos" value={profileData.tickets.abiertos} color="yellow" />
                    <StatBox label="En Proceso" value={profileData.tickets.en_proceso} color="blue" />
                    <StatBox label="Resueltos" value={profileData.tickets.resueltos} color="green" />
                    <StatBox label="Cerrados" value={profileData.tickets.cerrados} />
                    <StatBox label="Asignados" value={profileData.tickets.asignados} color="purple" />
                    <StatBox label="Asignados Activos" value={profileData.tickets.asignados_activos} color="orange" />
                  </div>
                </SummarySection>

                {/* Vacaciones */}
                <SummarySection icon={<Palmtree className="w-4 h-4" />} title="Vacaciones" color="green">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <StatBox label="Días Disponibles" value={profileData.vacaciones.dias_disponibles_total} color="green" />
                    <StatBox label="Días Tomados" value={profileData.vacaciones.dias_usados_total} color="blue" />
                    <StatBox label="Días Pendientes" value={profileData.vacaciones.dias_pendientes} color="yellow" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <StatBox label="Sol. Aprobadas" value={profileData.vacaciones.solicitudes_aprobadas} color="green" />
                    <StatBox label="Sol. Pendientes" value={profileData.vacaciones.solicitudes_pendientes} color="yellow" />
                    <StatBox label="Sol. Rechazadas" value={profileData.vacaciones.solicitudes_rechazadas} color="red" />
                  </div>
                  {profileData.vacaciones.detalle_por_ano?.length > 0 && (
                    <div className="mt-3 text-xs">
                      <p className="font-medium text-slate-600 dark:text-slate-400 mb-1">Detalle por año:</p>
                      <div className="flex flex-wrap gap-2">
                        {profileData.vacaciones.detalle_por_ano.map(d => (
                          <span key={d.year} className="bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-600 dark:text-slate-300">
                            {d.year}: {d.disponibles - d.usados} días restantes ({d.usados}/{d.disponibles})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </SummarySection>

                {/* Permisos */}
                <SummarySection icon={<Clock className="w-4 h-4" />} title="Permisos" color="orange">
                  <div className="grid grid-cols-3 gap-3 mb-3">
                    <StatBox label="Horas Totales" value={profileData.permisos.horas_totales} />
                    <StatBox label="Horas Usadas" value={profileData.permisos.horas_usadas} color="blue" />
                    <StatBox label="Horas Disponibles" value={profileData.permisos.horas_disponibles} color="green" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <StatBox label="Aprobados" value={profileData.permisos.solicitudes_aprobadas} color="green" />
                    <StatBox label="Pendientes" value={profileData.permisos.solicitudes_pendientes} color="yellow" />
                    <StatBox label="Rechazados" value={profileData.permisos.solicitudes_rechazadas} color="red" />
                  </div>
                </SummarySection>

                {/* Capacitaciones */}
                <SummarySection icon={<GraduationCap className="w-4 h-4" />} title="Capacitaciones" color="teal">
                  <div className="grid grid-cols-3 gap-3">
                    <StatBox label="Cursos Inscritos" value={profileData.capacitaciones.cursos_inscritos} />
                    <StatBox label="Videos Completados" value={profileData.capacitaciones.videos_completados} color="green" />
                    <StatBox label="Videos en Progreso" value={profileData.capacitaciones.videos_en_progreso} color="blue" />
                  </div>
                </SummarySection>

                {/* Exámenes */}
                <SummarySection icon={<FileCheck className="w-4 h-4" />} title="Exámenes" color="indigo">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <StatBox label="Asignados" value={profileData.examenes.total_asignados} />
                    <StatBox label="Completados" value={profileData.examenes.completados} color="green" />
                    <StatBox label="Pendientes" value={profileData.examenes.pendientes} color="yellow" />
                    <StatBox label="Promedio" value={`${profileData.examenes.promedio_puntuacion}%`} color="blue" />
                  </div>
                </SummarySection>

                {/* Otros */}
                <SummarySection icon={<ClipboardList className="w-4 h-4" />} title="Otros" color="slate">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <StatBox label="Caja Chica" value={profileData.otros.caja_chica_registros} />
                    <StatBox label="Requisiciones" value={profileData.otros.requisiciones_total} />
                    <StatBox label="Req. Pendientes" value={profileData.otros.requisiciones_pendientes} color="yellow" />
                  </div>
                </SummarySection>
              </div>
            ) : null}

            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowProfileDialog(false)} className="w-full">
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

// Helper components for the summary
function SummarySection({ icon, title, color, children }) {
  const borderColors = {
    blue: "border-l-blue-500",
    purple: "border-l-purple-500",
    green: "border-l-green-500",
    orange: "border-l-orange-500",
    teal: "border-l-teal-500",
    indigo: "border-l-indigo-500",
    slate: "border-l-slate-500",
  };
  return (
    <div className={`border-l-4 ${borderColors[color] || "border-l-slate-400"} bg-white dark:bg-slate-800 rounded-lg p-4 shadow-sm`}>
      <h4 className="font-semibold text-sm text-slate-800 dark:text-white flex items-center gap-2 mb-3">
        {icon} {title}
      </h4>
      {children}
    </div>
  );
}

function StatBox({ label, value, color }) {
  const colors = {
    yellow: "text-yellow-600",
    blue: "text-blue-600",
    green: "text-green-600",
    red: "text-red-600",
    orange: "text-orange-600",
    purple: "text-purple-600",
  };
  return (
    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-md p-2 text-center">
      <p className={`text-lg font-bold ${colors[color] || "text-slate-700 dark:text-white"}`}>{value}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
}
